<script>
import { mapActions, mapState } from 'vuex';
import { SearchOutlined } from '@ant-design/icons-vue';
import bem from '@/utils/bem';
import columns from './columns';

const b = bem('reports-list');

export default {
  components: {
    SearchOutlined,
  },
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      searchText: '',
      searchedColumn: '',
      currentDataSource: [],
      filteredInfo: {},
    };
  },
  computed: {
    ...mapState({
      reports: (state) => state.reports.reports,
      total: (state) => state.reports.total,
    }),
    statusFilters() {
      if (!Array.isArray(this.reports)) return [];
      const statuses = [...new Set(this.reports.map((r) => r.status?.name).filter(Boolean))];
      return statuses.map((status) => ({ text: status, value: status }));
    },
    paymentMethodsFilters() {
      if (!Array.isArray(this.reports)) return [];
      const methods = [];
      this.reports.forEach((report) => {
        if (report.pay?.methods && Array.isArray(report.pay.methods)) {
          report.pay.methods.forEach((method) => {
            if (method && !methods.includes(method)) {
              methods.push(method);
            }
          });
        }
      });
      return methods.map((method) => ({ text: method, value: method }));
    },
    isRushFilters() {
      if (!Array.isArray(this.reports)) return [];
      return [
        { text: 'No', value: false },
        { text: 'Yes', value: true },
      ];
    },
    filteredReports() {
      return this.currentDataSource.length > 0 ? this.currentDataSource : (this.reports || []);
    },
    tableColumns() {
      return columns(
        this.statusFilters,
        this.paymentMethodsFilters,
        this.isRushFilters,
        this.filteredReports,
        this.filteredInfo,
      );
    },
    activeFilters() {
      return Object.keys(this.filteredInfo).filter((key) => {
        const value = this.filteredInfo[key];
        return value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : value !== '');
      });
    },
  },
  watch: {
    companyId() {
      this.getReportsData();
      this.filteredInfo = {};
    },
    reports: {
      handler(newReports) {
        this.currentDataSource = newReports || [];
      },
      immediate: true,
    },
  },
  mounted() {
    this.getReportsData();
  },
  methods: {
    ...mapActions('reports', ['getReports']),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    getReportsData() {
      this.getReports({
        companyId: this.companyId,
      });
    },
    handleTableChange(pagination, filters, sorter, { currentDataSource }) {
      this.currentDataSource = currentDataSource;
      this.filteredInfo = filters;
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      [this.searchText] = selectedKeys;
      this.searchedColumn = dataIndex;
    },
    handleReset(clearFilters) {
      clearFilters();
      this.searchText = '';
    },
    removeFilter(key) {
      const newFilteredInfo = { ...this.filteredInfo };
      newFilteredInfo[key] = null;
      this.filteredInfo = newFilteredInfo;
    },
    clearAllFilters() {
      this.filteredInfo = {};
    },
    getFilterLabel(key, value) {
      const column = this.tableColumns.find((col) => col.key === key || col.dataIndex === key);
      const title = column ? column.title : key;
      if (key === 'order.addedAt' && Array.isArray(value) && value.length === 2) {
        const start = new Date(value[0]).toLocaleDateString();
        const end = new Date(value[1]).toLocaleDateString();
        return `${title}: ${start} - ${end}`;
      }
      if (Array.isArray(value)) {
        return `${title}: ${value.join(', ')}`;
      }
      if (typeof value === 'boolean') {
        return `${title}: ${value ? 'Yes' : 'No'}`;
      }

      return `${title}: ${value}`;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    formatPrice(price) {
      if (!price && price !== 0) return '-';
      return `$${Number(price).toFixed(2)}`;
    },
    formatUserName(user) {
      if (!user || !user.firstName) return 'website';
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'website';
    },
    formatAddress(address) {
      if (!address) return '-';
      return `${address.streetAddress || ''}, ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}`.trim() || '-';
    },
  },
};
</script>

<template>
  <div :class="b()">
    <div
      v-if="activeFilters.length > 0"
      :class="b('active-filters')"
      style="margin-bottom: 16px; padding: 16px; background: #f5f5f5; border-radius: 4px;"
    >
      <div style="margin-bottom: 8px; font-weight: bold;">Active filters:</div>
      <a-tag
        v-for="key in activeFilters"
        :key="key"
        closable
        @close="removeFilter(key)"
        color="blue"
        style="margin-bottom: 8px;"
      >
        {{ getFilterLabel(key, filteredInfo[key]) }}
      </a-tag>
      <a-button type="link" size="small" @click="clearAllFilters">Clear all</a-button>
    </div>

    <a-table
      :columns="tableColumns"
      :dataSource="reports"
      :pagination="{ pageSize: 10 }"
      :rowKey="(record) => record.order.id"
      :scroll="{ x: 2500 }"
      @change="handleTableChange"
    >
      <template
        #customFilterDropdown="{
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          column,
        }"
      >
        <div style="padding: 8px">
          <template v-if="column.key === 'order.addedAt'">
            <a-range-picker
              :value="selectedKeys[0]"
              @change="(dates) => setSelectedKeys(dates ? [dates] : [])"
              style="width: 100%; margin-bottom: 8px; display: block"
            />
            <a-button
              type="primary"
              size="small"
              style="width: 90px; margin-right: 8px"
              @click="() => confirm()"
            >
              Filter
            </a-button>
            <a-button size="small" style="width: 90px" @click="() => clearFilters()">
              Reset
            </a-button>
          </template>
          <template v-else>
            <a-input
              ref="searchInput"
              :placeholder="`Search ${column.title}`"
              :value="selectedKeys[0]"
              style="width: 188px; margin-bottom: 8px; display: block"
              @change="(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])"
              @pressEnter="() => confirm()"
            />
            <a-button
              type="primary"
              size="small"
              style="width: 90px; margin-right: 8px"
              @click="() => confirm()"
            >
              <template #icon><SearchOutlined /></template>
              Search
            </a-button>
            <a-button size="small" style="width: 90px" @click="() => clearFilters()">
              Reset
            </a-button>
          </template>
        </div>
      </template>
      <template #customFilterIcon="{ filtered }">
        <SearchOutlined :style="{ color: filtered ? '#1890ff' : undefined }" />
      </template>

      <template #bodyCell="{ column, text }">
        <template v-if="column.key === 'order.addedAt'">
          <span>{{ formatDate(text) }}</span>
        </template>
        <template v-if="column.key === 'concierge'">
          <span>{{ formatUserName(text) }}</span>
        </template>
        <template v-if="column.key === 'billing'">
          <span>{{ formatAddress(text) }}</span>
        </template>
        <template v-if="column.key === 'shipping'">
          <span>{{ formatAddress(text) }}</span>
        </template>
        <template v-if="column.key === 'creator'">
          <span>{{ formatUserName(text) }}</span>
        </template>
        <template
          v-if="
            column.key === 'order.price' ||
            column.key === 'order.taxAmount' ||
            column.key === 'pay.taxAmount' ||
            column.key === 'order.discountAmount' ||
            column.key === 'pay.payed' ||
            column.key === 'pay.needToPay'
          "
        >
          <span>{{ formatPrice(text) }}</span>
        </template>
        <template v-if="column.key === 'pay.methods'">
          <span v-if="text && text.length">
            {{ text.join(', ') }}
          </span>
          <span v-else>-</span>
        </template>
        <template v-if="column.key === 'order.isQuickly'">
          <span>{{ text ? 'Yes' : 'No' }}</span>
        </template>
        <template v-if="column.key === 'promoCode.name'">
          <span>{{ text || '-' }}</span>
        </template>
      </template>
    </a-table>
  </div>
</template>

<style lang="scss">
@import './style.scss';
</style>
